import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { TagClient } from '@app-services/api/clients/tag.client';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatchError } from '@app-services/errors/error-matcher';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingButtonComponent } from '@app-components/common/loading-button/loading-button.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { DrawerService } from '@app-services/drawer.service';
import {
  DEFAULT_TAG_COLOR_ID,
  TagColorSelectComponent,
} from '@app-components/common/tag-color-select/tag-color-select.component';
import { TagContract } from '@app-types/api/tag';
import { TagType } from '@app-types/enums/tags';

@Component({
  selector: 'app-mail-account-tag-create-dialog',
  templateUrl: './mail-account-tag-create-dialog.component.html',
  styleUrls: ['./mail-account-tag-create-dialog.component.scss'],
  imports: [
    TranslateModule,
    LoadingButtonComponent,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatInputModule,
    TagColorSelectComponent,
  ],
})
export class MailAccountTagCreateDialogComponent implements OnInit, OnDestroy {
  @Input() mailAccountId: number;
  @Input() public editingTag: TagContract | null = null;
  @Output() public submitEventEmitter = new EventEmitter();
  @Output() public closeEventEmitter = new EventEmitter();

  public isLoading = false;

  public form = new UntypedFormGroup({
    tag: new UntypedFormControl(this.editingTag?.tag ?? '', [
      Validators.required,
      Validators.maxLength(255),
    ]),
    color: new UntypedFormControl(
      this.editingTag?.color ?? DEFAULT_TAG_COLOR_ID,
      [Validators.required]
    ),
  });

  constructor(
    private tagClient: TagClient,
    public matchError: MatchError,
    private drawerService: DrawerService
  ) {}

  ngOnInit(): void {
    this.editingTag &&
      this.form.setValue({
        tag: this.editingTag.tag,
        color: this.editingTag?.color ?? DEFAULT_TAG_COLOR_ID,
      });
  }

  public async onSubmit(): Promise<void> {
    if (this.form.valid) {
      this.isLoading = true;
      this.drawerService.disabledDrawer(true);
      try {
        const values = this.form.value;
        const infoTag = {
          mailAccountId: this.mailAccountId,
          tag: values.tag,
          color: values.color,
          type: TagType.Email,
        };

        this.editingTag
          ? await this.tagClient.updateTag({
              ...infoTag,
              tagId: this.editingTag.tagId,
            })
          : await this.tagClient.insert(infoTag);

        this.drawerService.disabledDrawer(false);
        this.form.setValue({
          tag: '',
          color: DEFAULT_TAG_COLOR_ID,
        });
        this.submitEventEmitter?.emit();
        this.drawerService.closeDrawer();
      } catch (e) {
        this.drawerService.disabledDrawer(false);
        this.matchError.errorHandler(e);
        this.matchError.logError(e);
      } finally {
        this.isLoading = false;
      }
    }
  }

  getErrorMessage(): string {
    const errors = this.form.get('tag')?.errors;
    if (!errors) return '';

    if (errors?.required) return 'tagIsRequired';
    if (errors?.maxlength) return 'moreThan255Char';

    return '';
  }

  ngOnDestroy(): void {
    this.closeEventEmitter?.emit();
  }
}
